import React, { lazy, Suspense, useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";

import DateFnsUtils from "@date-io/date-fns";
import { enGB } from "date-fns/locale";
import PropTypes from "prop-types";

import CssBaseline from "@material-ui/core/CssBaseline";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";

import Loader from "../../Components/Loaders";
import PrivateRoute from "../../Components/PrivateRoute";

import actions from "../../Stores/Auth/actions";
import { fetchBodyRegions } from "../../Stores/BodyRegions/actions";
import useItemValidator from "../../utils/useItemValidator";
import Alerts from "../Alerts";
import Login from "../Login";
import PackageTabs from "../PackageTabs";
import ReviewBadge from "../ReviewBadge";
import ReviewCard from "../ReviewCard";
import ReviewExport from "../ReviewExport";
import ReviewPoster from "../ReviewPoster";
import ReviewScore from "../ReviewScore";
import ViewAction from "../Survey/SurveyAction/ViewAction";
import Website from "../Website";
import Pages from "../Website/Pages";
import PagesAdd from "../Website/Pages/PageAdd";
import WebsiteAdd from "../Website/WebsiteAdd";

import WebsiteEnquiresAdd from "../Location/WebsiteEnquires/WebsiteEnquiresAdd";

import ResidentRoutes from "../../routes/ResidentRoutes";
import { generateHmac } from "../../utils/crypto";
import usePermissionAccess from "../../utils/hooks/usePermissionAccess";
import HubAdd from "../CareHomeGroups/Hub/HubAdd";
import WebsiteTabs from "../Location/WebsiteEnquires/WebsiteTabs";

const { check } = actions;

const DashBoard = lazy(() => import("../DashBoard"));
const UserDashboard = lazy(() => import("../UserDashboard"));

const CompanyProfile = lazy(
  () => import("../CareHomeGroups/CareHomeGroupsAdd"),
);

const CompanyTabs = lazy(
  () => import("../CareHomeGroups/CareHomeGroupsAdd/CompanyTabs"),
);

const UserManagement = lazy(() => import("../UserManagement"));
const Admins = lazy(() => import("../CareHomeGroups/Admins"));
const Staffs = lazy(() => import("../CareHomeGroups/Staffs"));

const Reviews = lazy(() => import("../Reviews"));
const ReviewsEdit = lazy(() => import("../Reviews/ReviewsEdit"));
const ReviewsImage = lazy(() => import("../Reviews/ReviewsImage"));

const UploadCard = lazy(() => import("../UploadCard"));
const UploadCardAdd = lazy(() => import("../UploadCard/UploadCardAdd"));

const LocationEdit = lazy(() => import("../Location/LocationEdit"));
const LocationProfile = lazy(() => import("../Location/LocationProfile"));

const Enquiries = lazy(() => import("../Enquiries/index"));
const EnquiriesAdd = lazy(() => import("../Enquiries/EnquiriesAdd"));
const EnquiriesEdit = lazy(() => import("../Enquiries/EnquiriesEdit"));

const News = lazy(() => import("../News/index"));
const NewsAdd = lazy(() => import("../News/NewsAdd"));

const Network = lazy(() => import("../Network/index"));
const NetworkAdd = lazy(() => import("../Network/NetworkAdd"));

const Blogs = lazy(() => import("../Location/Blogs"));
const BlogAdd = lazy(() => import("../Location/Blogs/BlogsAdd"));

const WebsiteEnquires = lazy(() => import("../Location/WebsiteEnquires"));

const Faqs = lazy(() => import("../Location/FAQs"));
const FaqAdd = lazy(() => import("../Location/FAQs/FAQAdd"));

const EmailSettings = lazy(() => import("../Location/EmailSettings"));

const Invites = lazy(() => import("../Location/Invitees"));
const History = lazy(() => import("../Location/History"));

const NotFoundPage = lazy(() => import("../NotFound"));
const UserProfile = lazy(() => import("../UserProfile"));
const ChangePassword = lazy(() => import("../ChangePassword"));
const ResetPassword = lazy(() => import("../ResetPassword"));
const ForgotPassword = lazy(() => import("../ForgotPassword"));
const Register = lazy(() => import("../Register"));
const NoPermission = lazy(() => import("../NoPermission"));
const MyFeedback = lazy(() => import("../Feedback/MyFeedback"));
const FeedbackReview = lazy(() => import("../Feedback/FeedbackReview"));

const ReviewLink = lazy(() => import("../Promotion/Share"));

const Payment = lazy(() => import("../Promotion/Payment"));
const Confirmation = lazy(() => import("../Promotion/Payment/Confirmation"));
const LocationSelector = lazy(() => import("../Promotion/LocationSelector"));

const Task = lazy(() => import("../Task"));

const SurveyAdd = lazy(() => import("../Survey/SurveyAdd"));
const SurveyTabs = lazy(() => import("../Survey/SurveyTabs"));
const SurveyAddStepper = lazy(() => import("../Survey/SurveyAddStepper"));

const SurveyMain = lazy(() => import("../Survey/SurveyMain"));
const SurveyListing = lazy(() => import("../Survey/Listing"));
const ViewResponse = lazy(
  () => import("../Survey/SurveyResponses/ViewResponse"),
);

const ReportTabs = lazy(() => import("../Enquiries/ReportTabs"));

const Subscriptions = lazy(() => import("../Subscriptions"));
const SubLocationSelector = lazy(
  () => import("../Subscriptions/SubLocationSelector"),
);
const SubPayment = lazy(() => import("../Subscriptions/SubPayment"));
const SubConfirmation = lazy(
  () => import("../Subscriptions/SubPayment/Confirmation"),
);
const Dashboard = lazy(() => import("../Subscriptions/Dashboard"));
const AddPayment = lazy(() => import("../Subscriptions/AddPayment"));

// Roster
const Roster = lazy(() => import("../Roster"));
const RosterCalendar = lazy(() => import("../Roster/RosterCalendar"));
const RosterAllocation = lazy(() => import("../Roster/RosterAllocation"));
const Employee = lazy(() => import("../Roster/Employee"));
const EmployeeAdd = lazy(() => import("../Roster/Employee/EmployeeAdd"));
const EmployeeEdit = lazy(() => import("../Roster/Employee/EmployeeEdit"));
const EmployeeEditV2 = lazy(() => import("../Roster/Employee/EmployeeEditV2"));
const EmployeeCSV = lazy(() => import("../Roster/Employee/EmployeeCSV"));
const RosterRole = lazy(() => import("../Roster/Role"));
const RosterRoleAdd = lazy(() => import("../Roster/Role/RoleAdd"));
const LeaveTabs = lazy(() => import("../Roster/Leave/LeaveTabs"));
const LeaveHistory = lazy(() => import("../Roster/Leave/LeaveHistory"));
const Timesheet = lazy(() => import("../Roster/Timesheet"));
const TimesheetAllList = lazy(() => import("../Roster/Timesheet/AllList"));
const Payroll = lazy(() => import("../Roster/Timesheet/Payroll"));
const TimeAndBudgetAnalysis = lazy(
  () => import("../Roster/Timesheet/TimeAndBudgetAnalysis"),
);
const WorkHoursReportPage = lazy(
  () => import("../Roster/Timesheet/WorkHoursReportPage"),
);
const Budget = lazy(() => import("../Roster/Budget"));
const BudgetAdd = lazy(() => import("../Roster/Budget/BudgetAdd"));
const RosterSettings = lazy(() => import("../Roster/Settings/RosterSettings"));
const Discrepancy = lazy(() => import("../Roster/Discrepancy"));
const Availability = lazy(() => import("../Roster/Availability"));

const Users = lazy(() => import("../Users"));
const UserEdit = lazy(() => import("../Roster/Employee/UserEdit"));

const Terminal = lazy(() => import("../Roster/Terminal"));
const TerminalAdd = lazy(() => import("../Roster/Terminal/TerminalAdd"));

const Team = lazy(() => import("../Roster/Team"));
const TeamAdd = lazy(() => import("../Roster/Team/TeamAdd"));

const Notification = lazy(() => import("../Roster/Notification"));

const Feed = lazy(() => import("../Roster/Feed"));
const AddFeed = lazy(() => import("../Roster/Feed/AddFeed"));
const FeedComment = lazy(() => import("../Roster/Feed/FeedComment"));
const FeedTabs = lazy(() => import("../Roster/Feed/FeedTabs"));
const FolderMain = lazy(() => import("../Policy/FolderMain"));
const FilePreview = lazy(() => import("../Policy/FilePreview"));

const TemplateForm = lazy(() => import("../Templates/TemplateForm"));
const Onboarding = lazy(() => import("../Roster/Employee/Onboarding"));
const OnboardingFlow = lazy(() => import("../Roster/OnboardingFlow"));
const OnboardingRevision = lazy(
  () => import("../Roster/OnboardingFlow/Revision"),
);
const DocumentResubmission = lazy(
  () => import("../Roster/Employee/DocumentResubmission"),
);
const ComplianceMatrix = lazy(
  () => import("../Roster/Employee/ComplianceMatrix"),
);

// Email Unsubscribe
const EmailUnsubscribe = lazy(() => import("../EmailUnsubscribe"));

// Maintenance
const MaintenanceTemplates = lazy(() => import("../Maintenance/Templates"));
const MaintenanceScheduleTabs = lazy(
  () => import("../Maintenance/Tabs/MaintenanceScheduleTabs"),
);
const MaintenanceInspectionTabs = lazy(
  () => import("../Maintenance/Tabs/MaintenanceInspectionTabs"),
);
const MaintenanceAssets = lazy(() => import("../Maintenance/Assets"));
const MaintenanceRooms = lazy(() => import("../Maintenance/Rooms"));
const KitchenPullList = lazy(() => import("../Kitchen/PullList"));
const Handover = lazy(() => import("../Handover"));
const DailyRecords = lazy(() => import("../DailyRecords"));
const ActivityHub = lazy(() => import("../ActivityHub/Hub"));
const ChartBoard = lazy(() => import("../ChartBoard"));
const LibertyReview = lazy(() => import("../LibertyReview"));
const MaintenanceRequests = lazy(() => import("../Maintenance/Requests"));
const MaintenanceRequestDetail = lazy(
  () => import("../Maintenance/Requests/MaintenanceRequestDetail"),
);
const MaintenanceInspectionDetail = lazy(
  () => import("../Maintenance/Inspections/MaintenanceInspectionDetail"),
);
const MaintenanceCompliance = lazy(() => import("../Maintenance/Compliance"));
const MaintenanceReportTabs = lazy(
  () => import("../Maintenance/Tabs/MaintenanceReportTabs"),
);
const MaintenanceComplianceAddRedirect = lazy(
  () => import("../Maintenance/Compliance/ComplianceAddRouteRedirect"),
);
const ComplianceEditRouteRedirect = lazy(
  () => import("../Maintenance/Compliance/ComplianceEditRouteRedirect"),
);

// Visitors
const Visitors = lazy(() => import("../Visitors"));
const VisitorAdd = lazy(() => import("../Visitors/VisitorAdd"));
const VisitorMain = lazy(() => import("../Visitors/VisitorMain"));
const VisitorList = lazy(() => import("../Visitors/VistorList"));

// Leave Accrual Reporting
const LeaveAccrualReporting = lazy(
  () => import("../Roster/Leave/AccrualReporting"),
);

// Resident
const Information = lazy(
  () => import("../Resident/ResidentOnboarding/Information"),
);
const ResidentUploadDoc = lazy(() => import("../Resident/ResidentUploadDoc"));

const SettingsTab = lazy(() => import("../Roster/Settings/SettingsTab"));

function RootApp({
  isAuthenticating,
  isAuthenticated,
  checkAuth,
  loadBodyRegions,
  user,
}) {
  const history = useHistory();
  const location = useLocation();
  const currentPath = location.pathname;
  const [authFallback, setAuthFallback] = useState(true);
  const [request, setRequest] = useState(false);
  const itemValidator = useItemValidator();
  const { hasItemAccess } = usePermissionAccess();

  const publicRoutes = [
    "/reset-password",
    "/onboarding",
    "/unsubscribe",
    "/visitor-book",
    "/location/upload-document",
    "/set-password",
  ];

  const loadIntercomScript = (appId) => {
    return new Promise((resolve, reject) => {
      if (!appId) return reject("Missing Intercom app ID");

      // Already loaded
      if (window.Intercom) return resolve(window.Intercom);

      // Check if script already exists
      if (document.getElementById("intercom-script")) {
        const checkLoaded = setInterval(() => {
          if (window.Intercom) {
            clearInterval(checkLoaded);
            resolve(window.Intercom);
          }
        }, 100);
        return;
      }

      const script = document.createElement("script");
      script.id = "intercom-script";
      script.src = `https://widget.intercom.io/widget/${appId}`;
      script.async = true;

      script.onload = () => {
        resolve(window.Intercom);
      };
      script.onerror = () => {
        reject();
      };

      document.head.appendChild(script);
    });
  };

  useEffect(() => {
    const appId = process.env.REACT_APP_INTERCOM_ID;
    const isPublicRoute = publicRoutes.some((route) =>
      currentPath.startsWith(route),
    );

    const init = async () => {
      if (!user || !user.id || !user.email || isPublicRoute) {
        if (window.Intercom && isPublicRoute) {
          window.Intercom("shutdown");
        }
        return;
      }

      try {
        await loadIntercomScript(appId);

        if (window.Intercom) {
          // Resident edit screens (Profile/Medications/Care Plan/Observations/...)
          // carry their own Smart Feedback entry point — the Intercom launcher
          // bubble competes with it in the same corner, so hide it there.
          const isResidentEditRoute =
            currentPath.startsWith("/d/resident/edit/");
          window.Intercom("boot", {
            app_id: appId,
            name: `${user.first_name} ${user.last_name}`,
            email: user.email,
            user_id: user.id,
            user_hash: generateHmac(user.id, process.env.REACT_APP_HMAC_SECRET),
            created_at: Math.floor(Date.now() / 1000), // seconds!
            Role: user?.accounting_type ?? "Not Specified",
            CareHome: user?.default_location?.title || "Not Specified",
            CareRole: user?.accounting_type ?? "Not Specified",
            hide_default_launcher: isResidentEditRoute,
          });
        }
      } catch (error) {
        console.error("Error initializing Intercom:", error);
      }
    };

    init();

    // cleanup on unmount
    return () => {
      if (window.Intercom) {
        window.Intercom("shutdown");
      }
    };
  }, [user, currentPath]);

  const validateNotiRedirect = () => {
    const query = new URLSearchParams(history.location.search);
    const redirect = query.get("redirectToHelp");
    if (redirect) {
      window.open(
        "https://help.carecentre.io/en/articles/11600769-managing-notification-settings-in-carecentre-io",
        "_blank",
      );
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const isPublicRoute = publicRoutes.some((route) =>
      currentPath.startsWith(route),
    );

    const isNotiSetting = currentPath.includes("notification-settings");

    if (token) {
      checkAuth(token, history);
      setRequest(true);
      if (user?.id) {
        // initializeIntercom();
      }
    } else {
      setAuthFallback(false);
      if (isNotiSetting) validateNotiRedirect();
      if (!isPublicRoute) {
        history.replace({
          pathname: "/",
          state: {
            referrer: history.location,
            params: history.location.pathname,
          },
        });
      }
    }
  }, []);

  // Re-run checkAuth when tab becomes visible (keeps last_used_at fresh
  // and triggers sliding window refresh for long-lived SPA sessions)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const token = localStorage.getItem("token");
        if (token) {
          // checkAuth(token, history);
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [history]);

  useEffect(() => {
    if (isAuthenticated && !isAuthenticating) {
      setAuthFallback(false);
      setTimeout(() => {
        setRequest(false);
      }, 1000);
    }
  }, [isAuthenticated, isAuthenticating]);

  /**
   * The body-region vocabulary, fetched once at boot — NOT when a body map first
   * paints.
   *
   * Every form that writes a `body_location` (the observation recorder, the wound
   * and pain charts) reads the vocabulary synchronously from the module singleton
   * in Components/BodyMap/bodyRegions. None of them mounts a body-map figure, so
   * while this fetch hung off the figure's own hook they were all picking sites
   * out of the BUNDLED FALLBACK — a build-time snapshot that goes stale the moment
   * the API's region list moves. A nurse could record a wound against a key the
   * server no longer knows.
   *
   * The saga is a no-op once the payload is live (and serves a version-keyed
   * localStorage copy meanwhile), so this costs one request per session.
   */
  useEffect(() => {
    if (isAuthenticated) loadBodyRegions();
  }, [isAuthenticated, loadBodyRegions]);

  return (
    <>
      <Alerts />
      <Suspense fallback={<Loader open />}>
        {isAuthenticating || authFallback || request ? (
          <>
            <Loader open />
          </>
        ) : (
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={enGB}>
            <CssBaseline />
            <Switch>
              {/* AUTH STARTS */}
              <Route path="/" exact component={Login} />
              <Route path="/login" component={Login} />
              <Route
                path="/reset-password/:email/:token"
                component={ResetPassword}
              />
              <Route path="/admin/register" component={Register} />
              <Route path="/forgot/password" component={ForgotPassword} />
              <Route
                path="/set-password/:id"
                component={OnboardingFlow}
                exact
              />
              <Route path="/visitor-book/:id" component={VisitorMain} exact />
              <Route
                path="/location/upload-document/:id"
                component={ResidentUploadDoc}
                exact
              />
              <Route
                path="/onboarding/revision/:id"
                exact
                render={() => <OnboardingRevision />}
              />
              <Route
                path="/document/resubmission/:id"
                exact
                render={() => <DocumentResubmission />}
              />
              <Route
                path="/unsubscribe"
                exact
                render={() => <EmailUnsubscribe />}
              />

              {/* AUTH ENDS */}

              <PrivateRoute path="/d">
                <DashBoard>
                  <Suspense fallback={<Loader open />}>
                    <Switch>
                      <Route exact path="/d/home" component={UserDashboard} />
                      <Route
                        exact
                        path="/d/rota/week"
                        render={() =>
                          itemValidator(<RosterCalendar />, `ROSTER`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/month"
                        component={RosterAllocation}
                      />
                      <Route
                        exact
                        path="/d/r/hr/roles"
                        render={() =>
                          itemValidator(
                            <RosterRole />,
                            `ROSTER-ROLES`,
                            `DISPLAY`,
                          )
                        }
                      />

                      <Route
                        exact
                        path="/d/hr/employee"
                        render={() =>
                          itemValidator(
                            <Employee isUser={false} />,
                            `EMPLOYEES`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/upload"
                        render={() =>
                          itemValidator(
                            <EmployeeCSV isUser={false} />,
                            `EMPLOYEES`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/add"
                        render={() =>
                          itemValidator(
                            <EmployeeAdd isUser={false} />,
                            `EMPLOYEES`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id"
                        render={() =>
                          itemValidator(
                            <EmployeeEditV2 />,
                            `EMPLOYEES`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id/personal"
                        render={() =>
                          itemValidator(
                            <EmployeeEditV2 />,
                            `EMPLOYEES`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id/wage-and-salary"
                        render={() =>
                          itemValidator(
                            <EmployeeEditV2 />,
                            `EMPLOYEES`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id/compliance-tracker"
                        render={() =>
                          itemValidator(
                            <EmployeeEditV2 />,
                            `EMPLOYEES`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id/locations"
                        render={() =>
                          itemValidator(<EmployeeEdit />, `EMPLOYEES`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id/schedule"
                        render={() =>
                          itemValidator(<EmployeeEdit />, `EMPLOYEES`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id/documents"
                        render={() =>
                          itemValidator(
                            <EmployeeEditV2 />,
                            `EMPLOYEES`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id/time"
                        render={() =>
                          itemValidator(<EmployeeEditV2 />, `LEAVE`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/employee/edit/:id/payroll"
                        render={() =>
                          itemValidator(
                            <EmployeeEditV2 />,
                            `EMPLOYEES`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/roles"
                        render={() =>
                          itemValidator(
                            <RosterRole />,
                            `ROSTER-ROLES`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/roles/add"
                        render={() =>
                          itemValidator(
                            <RosterRoleAdd />,
                            `ROSTER-ROLES`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/roles/edit/:id"
                        render={() =>
                          itemValidator(
                            <RosterRoleAdd />,
                            `ROSTER-ROLES`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/leave/calendar"
                        render={() =>
                          user?.can_manage_leave &&
                          !hasItemAccess(`LEAVE`, `DISPLAY`) ? (
                            <Redirect to="/d/leave/requests" />
                          ) : (
                            itemValidator(<LeaveTabs />, `LEAVE`, `DISPLAY`)
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/leave/requests"
                        render={() =>
                          user?.can_manage_leave ? (
                            <LeaveTabs />
                          ) : (
                            itemValidator(<LeaveTabs />, `LEAVE`, `DISPLAY`)
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/leave/manager"
                        render={() =>
                          user?.can_manage_leave ? (
                            <LeaveTabs />
                          ) : (
                            itemValidator(<LeaveTabs />, `LEAVE`, `DISPLAY`)
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/leave/accrual-reporting"
                        render={() =>
                          itemValidator(
                            <LeaveAccrualReporting />,
                            `LEAVE-ACCRUAL`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/leave/history"
                        render={() =>
                          itemValidator(<LeaveHistory />, `LEAVE`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/timesheet/list"
                        render={() =>
                          itemValidator(<Timesheet />, `TIMESHEET`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/timesheet/clock"
                        render={() =>
                          itemValidator(
                            <TimesheetAllList />,
                            `TIMESHEET-REPORT`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/timesheet/analysis"
                        render={() =>
                          itemValidator(
                            <TimeAndBudgetAnalysis />,
                            `TIMESHEET-REPORT`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/timesheet/work-hours-report"
                        render={() =>
                          itemValidator(
                            <WorkHoursReportPage />,
                            `TIMESHEET-REPORT`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/timesheet/payroll/:from_date/:to_date"
                        render={() =>
                          itemValidator(<Payroll />, `TIMESHEET`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/budget"
                        render={() =>
                          itemValidator(<Budget />, `BUDGET`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/budget/add"
                        render={() =>
                          itemValidator(<BudgetAdd />, `BUDGET`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/budget/edit/:id"
                        render={() =>
                          itemValidator(<BudgetAdd />, `BUDGET`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/company/settings"
                        render={() =>
                          itemValidator(
                            <SettingsTab />,
                            `ROSTER-SETTINGS`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/company/settings/location"
                        render={() =>
                          itemValidator(
                            <SettingsTab />,
                            `ROSTER-SETTINGS`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/company/settings/ai-credits"
                        render={() =>
                          itemValidator(
                            <SettingsTab />,
                            `ROSTER-SETTINGS`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/extra-time"
                        render={() =>
                          itemValidator(
                            <Discrepancy />,
                            `DISCREPANCY`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/availability"
                        render={() =>
                          itemValidator(<Availability />, `ROSTER`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/terminal"
                        render={() =>
                          itemValidator(<Terminal />, `ROSTER`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/terminal/add"
                        render={() =>
                          itemValidator(<TerminalAdd />, `ROSTER`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/rota/terminal/edit/:id"
                        render={() =>
                          itemValidator(<TerminalAdd />, `ROSTER`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/visitors/book"
                        render={() =>
                          itemValidator(<Visitors />, `VISITORS`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/visitors/book/add"
                        render={() =>
                          itemValidator(<VisitorAdd />, `VISITORS`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/visitors/book/edit/:id"
                        render={() =>
                          itemValidator(
                            <VisitorAdd isEditing={true} />,
                            `VISITORS`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/visitors/list"
                        render={() =>
                          itemValidator(<VisitorList />, `VISITORS`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/teams"
                        render={() =>
                          itemValidator(<Team />, `ROSTER`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/teams/add"
                        render={() =>
                          itemValidator(<TeamAdd />, `ROSTER`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/teams/edit/:id"
                        render={() =>
                          itemValidator(<TeamAdd />, `ROSTER`, `WRITE`)
                        }
                      />

                      <Route
                        exact
                        path="/d/feed/all"
                        render={() => itemValidator(<Feed />, `FEED`, `WRITE`)}
                      />
                      <Route
                        exact
                        path="/d/feed/add"
                        render={() =>
                          itemValidator(<AddFeed />, `FEED`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/feed/report"
                        render={() =>
                          itemValidator(<FeedTabs />, `FEED`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/feed/report/kudos-leaderboard"
                        render={() =>
                          itemValidator(<FeedTabs />, `FEED`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/feed/report/kudos-report"
                        render={() =>
                          itemValidator(<FeedTabs />, `FEED`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/feed/report/values-report"
                        render={() =>
                          itemValidator(<FeedTabs />, `FEED`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/feed/report/company-scorecard"
                        render={() =>
                          itemValidator(<FeedTabs />, `FEED`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/feed/edit/:id"
                        render={() =>
                          itemValidator(<AddFeed />, `FEED`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/feed/comments/:id"
                        render={() =>
                          itemValidator(<FeedComment />, `FEED`, `WRITE`)
                        }
                      />

                      {/* Roster End */}

                      <Route
                        exact
                        path="/d/no-permission"
                        component={NoPermission}
                      />
                      <Route
                        exact
                        path="/d/myprofile"
                        component={UserProfile}
                      />
                      <Route
                        exact
                        path="/d/myprofile/notification-settings"
                        component={Notification}
                      />
                      <Route
                        exact
                        path="/d/change-password"
                        component={ChangePassword}
                      />
                      <Route
                        exact
                        path="/d/company/overview/:id"
                        render={() => (
                          // itemValidator(
                          //   <CompanyProfile isViewing={true} />,
                          //   `COMPANY`,
                          //   `WRITE`
                          // )
                          <CompanyTabs />
                        )}
                      />
                      <Route
                        exact
                        path="/d/company/subscribe"
                        render={() => (
                          // itemValidator(
                          //   <CompanyProfile isViewing={true} />,
                          //   `COMPANY`,
                          //   `WRITE`
                          // )
                          <CompanyTabs />
                        )}
                      />
                      <Route
                        exact
                        path="/d/company/billing"
                        render={() => (
                          // itemValidator(
                          //   <CompanyProfile isViewing={true} />,
                          //   `COMPANY`,
                          //   `WRITE`
                          // )
                          <CompanyTabs />
                        )}
                      />
                      <Route
                        exact
                        path="/d/company/hub"
                        render={() => (
                          // itemValidator(
                          //   <CompanyProfile isViewing={true} />,
                          //   `COMPANY`,
                          //   `WRITE`
                          // )
                          <CompanyTabs />
                        )}
                      />
                      <Route
                        exact
                        path="/d/company/hub/add"
                        render={() => (
                          // itemValidator(
                          //   <CompanyProfile isViewing={true} />,
                          //   `COMPANY`,
                          //   `WRITE`
                          // )
                          <HubAdd />
                        )}
                      />
                      <Route
                        exact
                        path="/d/company/hub/edit/:id"
                        render={() => (
                          // itemValidator(
                          //   <CompanyProfile isViewing={true} />,
                          //   `COMPANY`,
                          //   `WRITE`
                          // )
                          <HubAdd />
                        )}
                      />

                      {/* LOCATION STARTS */}
                      <Route
                        exact
                        path="/d/payment/location-selector"
                        render={() =>
                          itemValidator(
                            <LocationSelector />,
                            `LOCATION`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/location/:id/profile/:step"
                        component={() =>
                          itemValidator(
                            <LocationSelector />,
                            `LOCATION`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile"
                        component={() =>
                          itemValidator(
                            <LocationProfile />,
                            `LOCATION`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/base-data"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/care-offered"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/facilities"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/funding-fees"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/covid-info"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/description"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/photos"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/videos"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/teams"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/tour"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/profile/team"
                        component={() =>
                          itemValidator(<LocationEdit />, `LOCATION`, `WRITE`)
                        }
                      />
                      {/* Location Ends */}
                      <Route
                        exact
                        path="/d/location/edit/:id/posts"
                        component={() =>
                          itemValidator(<Blogs />, `BLOGS`, `DISPLAY`, true)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/posts/add"
                        render={() =>
                          itemValidator(<BlogAdd />, `BLOGS`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/posts/edit/:postId"
                        render={() =>
                          itemValidator(<BlogAdd />, `BLOGS`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/website-enquiries/listing"
                        render={() =>
                          itemValidator(
                            <WebsiteTabs isEditing={true} />,
                            `LOCATION`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/website-enquiries/acquisition"
                        render={() =>
                          itemValidator(
                            <WebsiteTabs isEditing={true} />,
                            `LOCATION`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/website-enquiries/add"
                        component={() =>
                          itemValidator(
                            <WebsiteEnquiresAdd />,
                            `LOCATION`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/website-enquiries/edit/:webEnqID"
                        render={() =>
                          itemValidator(
                            <WebsiteEnquiresAdd />,
                            `LOCATION`,
                            `WRITE`,
                          )
                        }
                      />
                      {/* Enquiry Start */}
                      <Route
                        exact
                        path="/d/leads/:stage(all|prospecting|first contact|viewing|viewing outcome|assessment|assessment outcome|closed)/"
                        render={() =>
                          itemValidator(<Enquiries />, `ENQUIRIES`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/add"
                        render={() =>
                          itemValidator(<EnquiriesAdd />, `ENQUIRIES`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/edit/:id"
                        render={() =>
                          itemValidator(<EnquiriesEdit />, `ENQUIRIES`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/networking"
                        render={() =>
                          itemValidator(<Network />, `NETWORKING`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/task"
                        render={() =>
                          itemValidator(<Task />, `ENQUIRIES`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/networking/add"
                        render={() =>
                          itemValidator(<NetworkAdd />, `NETWORKING`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/networking/edit/:id"
                        render={() =>
                          itemValidator(
                            <NetworkAdd isEditing={true} />,
                            `NETWORKING`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/reports/dashboard"
                        render={() =>
                          itemValidator(
                            <ReportTabs isEditing={true} />,
                            `ENQUIRIES`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/reports/close"
                        render={() =>
                          itemValidator(
                            <ReportTabs isEditing={true} />,
                            `ENQUIRIES`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/reports/acquisition"
                        render={() =>
                          itemValidator(
                            <ReportTabs isEditing={true} />,
                            `ENQUIRIES`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/leads/reports/geography"
                        render={() =>
                          itemValidator(
                            <ReportTabs isEditing={true} />,
                            `ENQUIRIES`,
                            `DISPLAY`,
                          )
                        }
                      />
                      {/* Enquiry End */}
                      {/* Post Start */}
                      <Route
                        exact
                        path="/d/post/news"
                        render={() =>
                          itemValidator(<News />, `NEWS`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/post/news/add"
                        render={() =>
                          itemValidator(<NewsAdd />, `NEWS`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/post/news/edit/:id"
                        render={() =>
                          itemValidator(
                            <NewsAdd isEditing={true} />,
                            `NEWS`,
                            `WRITE`,
                          )
                        }
                      />
                      {/* Post End */}
                      <Route
                        exact
                        path="/d/location/edit/:id/faqs"
                        render={() =>
                          itemValidator(<Faqs />, `FAQS`, `DISPLAY`, true)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/faqs/add"
                        render={() =>
                          itemValidator(<FaqAdd />, `FAQS`, `WRITE`, true)
                        }
                      />
                      <Route
                        exact
                        path="/d/location/edit/:id/faqs/edit/:faqID"
                        render={() =>
                          itemValidator(<FaqAdd />, `FAQS`, `WRITE`, true)
                        }
                      />
                      <Route
                        path="/d/location/edit/:id/email-settings"
                        render={() =>
                          itemValidator(
                            <EmailSettings />,
                            `EMAIL-SETTINGS`,
                            `WRITE`,
                            true,
                          )
                        }
                      />
                      {/* LOCATION ENDS */}

                      {/* SURVEY STARTS */}
                      <Route
                        exact
                        path="/d/engage/employees"
                        render={() =>
                          itemValidator(
                            <SurveyListing type="employees" />,
                            `SURVEY`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/residents"
                        render={() =>
                          itemValidator(
                            <SurveyListing type="residents" />,
                            `SURVEY`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/pulse"
                        render={() =>
                          itemValidator(
                            <SurveyListing type="pulse" />,
                            `SURVEY`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/employees/add"
                        render={() =>
                          itemValidator(
                            <SurveyAddStepper type="employees" />,
                            `SURVEY`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/residents/add"
                        render={() =>
                          itemValidator(
                            <SurveyAddStepper type="residents" />,
                            `SURVEY`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/resident/onboard"
                        render={() =>
                          itemValidator(<Information />, `RESIDENT`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/resident/onboard/:id"
                        render={() =>
                          itemValidator(
                            <Information isEditing={true} />,
                            `RESIDENT`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/pulse/add"
                        render={() =>
                          itemValidator(
                            <SurveyAddStepper type="pulse" />,
                            `SURVEY`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/edit/:id"
                        render={() =>
                          itemValidator(<SurveyTabs />, `SURVEY`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/edit/:id/qr"
                        render={() =>
                          itemValidator(<SurveyTabs />, `SURVEY`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/edit/:id/invite"
                        render={() =>
                          itemValidator(
                            <SurveyTabs />,
                            `SURVEY-INVITE`,
                            `WRITE`,
                          )
                        }
                      />

                      <Route
                        exact
                        path="/d/engage/edit/:id/responses"
                        render={() =>
                          itemValidator(
                            <SurveyTabs />,
                            `SURVEY-RESPONSES`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/reports/view/:type(employees|residents|pulse)/dashboard/:id"
                        render={() =>
                          itemValidator(<SurveyMain />, `SURVEY`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/reports/view/:type(employees|residents|pulse)/overview/:id"
                        render={() =>
                          itemValidator(<SurveyMain />, `SURVEY`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/reports/view/:type(employees|residents|pulse)/headlines/:id"
                        render={() =>
                          itemValidator(<SurveyMain />, `SURVEY`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/reports/view/:type(employees|residents|pulse)/action/:id"
                        render={() =>
                          itemValidator(<SurveyMain />, `SURVEY`, `DISPLAY`)
                        }
                      />
                      {/* <Route
                        exact
                        path="/d/engage/reports/view/:type(employees|residents|pulse)/finding/:id"
                        render={() =>
                          itemValidator(<SurveyMain />, `SURVEY`, `DISPLAY`)
                        }
                      /> */}
                      <Route
                        exact
                        path="/d/engage/reports/view/:type(employees|residents|pulse)/response/:id"
                        render={() =>
                          itemValidator(<SurveyMain />, `SURVEY`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/reports/view/:type(employees|residents|pulse)/comparisons/:id"
                        render={() =>
                          itemValidator(<SurveyMain />, `SURVEY`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/response/view/:id"
                        render={() =>
                          itemValidator(<ViewResponse />, `SURVEY`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/engage/action/:id"
                        render={() =>
                          itemValidator(<ViewAction />, `SURVEY`, `DISPLAY`)
                        }
                      />

                      {/* SURVEY ENDS */}

                      {/* USER MANAGEMENT STARTS */}
                      <Route
                        exact
                        path="/d/account-users"
                        component={() =>
                          itemValidator(<UserManagement />, `ADMIN`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/account-users/admin"
                        component={() =>
                          itemValidator(<Admins />, `ADMIN`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/account-users/staff"
                        component={() =>
                          itemValidator(<Staffs />, `STAFF`, `DISPLAY`)
                        }
                      />
                      {/* USER MANAGEMENT END */}
                      {/* REVIEW STARTS */}
                      <Route
                        exact
                        path="/d/review/listing"
                        render={() =>
                          itemValidator(<Reviews />, `REVIEWS`, `DISPLAY`, true)
                        }
                      />
                      <Route
                        exact
                        path="/d/review/review-card/"
                        render={() =>
                          itemValidator(
                            <ReviewCard />,
                            `REVIEW-CARD`,
                            `WRITE`,
                            true,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/review-poster/"
                        render={() =>
                          itemValidator(
                            <ReviewPoster />,
                            `REVIEW-POSTER`,
                            `WRITE`,
                            true,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/review-upload"
                        component={() =>
                          itemValidator(
                            <UploadCard />,
                            `REVIEWS`,
                            `DISPLAY`,
                            true,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/review-upload/add"
                        component={() =>
                          itemValidator(
                            <UploadCardAdd />,
                            `REVIEWS`,
                            `WRITE`,
                            true,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/edit/:id/reviews/edit/:reviewID"
                        component={() =>
                          itemValidator(
                            <ReviewsEdit />,
                            `REVIEWS`,
                            `WRITE`,
                            true,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/edit/:id/reviews/edit/:reviewID/generate-image"
                        component={() =>
                          itemValidator(
                            <ReviewsImage />,
                            `REVIEWS`,
                            `WRITE`,
                            true,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/share"
                        render={() =>
                          itemValidator(
                            <ReviewLink />,
                            `REVIEW-SHARE`,
                            `DISPLAY`,
                            true,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/packages"
                        render={() =>
                          itemValidator(
                            <PackageTabs />,
                            `PACKAGES`,
                            `DISPLAY`,
                            true,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/packages/order-history"
                        render={() =>
                          itemValidator(
                            <PackageTabs />,
                            `PACKAGES`,
                            `DISPLAY`,
                            true,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/export"
                        render={() =>
                          itemValidator(
                            <ReviewExport />,
                            `REVIEWS`,
                            `WRITE`,
                            true,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/invites"
                        render={() =>
                          itemValidator(
                            <Invites />,
                            `REVIEW-INVITE`,
                            `DISPLAY`,
                            true,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/orders"
                        render={() =>
                          itemValidator(<History />, `ORDERS`, `DISPLAY`, true)
                        }
                      />
                      <Route
                        exact
                        path="/d/review/review-score"
                        render={() =>
                          itemValidator(
                            <ReviewScore />,
                            `REVIEW-SCORE`,
                            `WRITE`,
                            true,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/review/review-badge"
                        render={() =>
                          itemValidator(
                            <ReviewBadge />,
                            `REVIEW-BADGE`,
                            `WRITE`,
                            true,
                          )
                        }
                      />
                      {/* Review Ends */}
                      {/* Subscription Starts */}
                      <Route
                        exact
                        path="/d/subscriptions/bundles"
                        render={() => <Subscriptions />}
                      />
                      <Route
                        exact
                        path="/d/subscriptions/bundles/location-selector"
                        render={() => <SubLocationSelector />}
                      />
                      <Route
                        exact
                        path="/d/subscriptions/bundles/payment"
                        render={() => <SubPayment />}
                      />
                      <Route
                        exact
                        path="/d/subscriptions/bundles/payment/confirmation"
                        render={() => <SubConfirmation />}
                      />
                      <Route
                        exact
                        path="/d/subscriptions/dashboard"
                        render={() => <Dashboard />}
                      />
                      <Route
                        exact
                        path="/d/subscriptions/add-payment"
                        render={() => <AddPayment />}
                      />
                      {/* Subscription Ends */}

                      {/* WEBSITE */}
                      <Route
                        exact
                        path="/d/website"
                        component={() =>
                          itemValidator(<Website />, `WEBSITE`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/website/add"
                        component={() =>
                          itemValidator(<WebsiteAdd />, `WEBSITE`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/website/edit/:id"
                        component={() =>
                          itemValidator(<WebsiteAdd />, `WEBSITE`, `WRITE`)
                        }
                      />
                      <Route
                        exact
                        path="/d/website/:id/pages"
                        component={() =>
                          itemValidator(<Pages />, `WEBSITE`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/website/:id/pages/:pageID"
                        component={() =>
                          itemValidator(<PagesAdd />, `WEBSITE`, `WRITE`)
                        }
                      />
                      {/* WEBSITE */}
                      {/* STRIPE PAYMENT PAGES */}
                      <Route
                        exact
                        path="/d/payment"
                        component={() =>
                          itemValidator(<Payment />, `PACKAGES`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/payment-confirmation"
                        component={() =>
                          itemValidator(<Confirmation />, `PACKAGES`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/users"
                        component={() =>
                          itemValidator(
                            <Employee isUser={true} />,
                            `EMPLOYEEUSER`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/users/edit/:id"
                        component={() =>
                          itemValidator(<UserEdit />, `EMPLOYEEUSER`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/users/add"
                        render={() =>
                          itemValidator(
                            <EmployeeAdd isUser={true} />,
                            `EMPLOYEEUSER`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/policy"
                        component={() =>
                          itemValidator(<FolderMain />, `POLICIES`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/policy/folder/:slug*"
                        component={() =>
                          itemValidator(
                            <FolderMain isViewing={true} />,
                            `POLICIES`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/policy/file/:path*"
                        component={() =>
                          itemValidator(<FilePreview />, `POLICIES`, `DISPLAY`)
                        }
                      />

                      <Route
                        exact
                        path="/d/hr/onboarding/template"
                        render={() =>
                          itemValidator(
                            <TemplateForm />,
                            `ONBOARDING`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/onboarding"
                        render={() =>
                          itemValidator(<Onboarding />, `EMPLOYEES`, `DISPLAY`)
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/onboarding/employee/:id"
                        render={() =>
                          itemValidator(
                            <Onboarding isEditing={true} />,
                            `EMPLOYEES`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/hr/compliance-matrix"
                        render={() =>
                          itemValidator(
                            <ComplianceMatrix />,
                            `ONBOARDING`,
                            `DISPLAY`,
                          )
                        }
                      />

                      {/* MAINTENANCE */}
                      <Redirect
                        exact
                        from="/d/maintenance/templates"
                        to="/d/maintenance/schedule/templates"
                      />
                      <Redirect
                        exact
                        from="/d/maintenance/inspection-templates"
                        to="/d/maintenance/schedule/templates"
                      />
                      <Redirect
                        exact
                        from="/d/maintenance/inspection/templates"
                        to="/d/maintenance/schedule/templates"
                      />
                      <Redirect
                        exact
                        from="/d/maintenance/inspections"
                        to="/d/maintenance/inspection"
                      />
                      <Redirect
                        exact
                        from="/d/maintenance/inspection/pending-approval"
                        to="/d/maintenance/inspection/approval"
                      />
                      <Route
                        exact
                        path="/d/maintenance/inspections/:id"
                        render={() =>
                          itemValidator(
                            <MaintenanceInspectionDetail />,
                            `MAINTENANCE`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        path="/d/maintenance/inspection"
                        render={() =>
                          itemValidator(
                            <MaintenanceInspectionTabs />,
                            `MAINTENANCE`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Redirect
                        exact
                        from="/d/maintenance/schedules"
                        to="/d/maintenance/schedule"
                      />
                      <Redirect
                        exact
                        from="/d/maintenance/schedule-templates"
                        to="/d/maintenance/schedule/templates"
                      />
                      <Route
                        path="/d/maintenance/schedule"
                        render={() =>
                          itemValidator(
                            <MaintenanceScheduleTabs />,
                            `MAINTENANCE`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/maintenance/assets"
                        render={() =>
                          itemValidator(
                            <MaintenanceAssets />,
                            `MAINTENANCE`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/maintenance/requests"
                        render={() =>
                          itemValidator(
                            <MaintenanceRequests />,
                            `MAINTENANCE`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/maintenance/requests/:id"
                        render={() =>
                          itemValidator(
                            <MaintenanceRequestDetail />,
                            `MAINTENANCE`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/maintenance/certificates/add"
                        render={() =>
                          itemValidator(
                            <MaintenanceComplianceAddRedirect />,
                            `MAINTENANCE`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/maintenance/certificates/edit/:uuid"
                        render={() =>
                          itemValidator(
                            <ComplianceEditRouteRedirect />,
                            `MAINTENANCE`,
                            `WRITE`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/maintenance/certificates"
                        render={() =>
                          itemValidator(
                            <MaintenanceCompliance />,
                            `MAINTENANCE`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        path="/d/maintenance/reports"
                        render={() =>
                          itemValidator(
                            <MaintenanceReportTabs />,
                            `MAINTENANCE`,
                            `DISPLAY`,
                          )
                        }
                      />
                      <Route
                        exact
                        path="/d/maintenance/rooms"
                        render={() =>
                          itemValidator(
                            <MaintenanceRooms />,
                            `MAINTENANCE`,
                            `DISPLAY`,
                          )
                        }
                      />
                      {/* MAINTENANCE ENDS */}

                      {/* Kitchen pull list — direct Route BEFORE the catch-all
                          ResidentRoutes group (which, lacking a `path`, always
                          matches in this Switch and would otherwise swallow it) */}
                      <Route
                        exact
                        path="/d/kitchen/pull-list"
                        render={() =>
                          itemValidator(
                            <KitchenPullList />,
                            `RESIDENT`,
                            `DISPLAY`,
                          )
                        }
                      />

                      {/* Shift handover digest — same direct-Route placement
                          as Kitchen (must precede the catch-all ResidentRoutes
                          group) */}
                      <Route
                        exact
                        path="/d/handover"
                        render={() =>
                          itemValidator(<Handover />, `RESIDENT`, `DISPLAY`)
                        }
                      />

                      {/* Daily records — home-wide day report of every
                          resident's observations as readable sentences. Same
                          direct-Route placement as Handover (must precede the
                          catch-all ResidentRoutes group) */}
                      <Route
                        exact
                        path="/d/daily-records"
                        render={() =>
                          itemValidator(<DailyRecords />, `RESIDENT`, `DISPLAY`)
                        }
                      />

                      {/* Activity Hub — same direct-Route placement as Kitchen
                          (must precede the catch-all ResidentRoutes group) */}
                      <Route
                        exact
                        path="/d/activity-hub"
                        render={() =>
                          itemValidator(<ActivityHub />, `RESIDENT`, `DISPLAY`)
                        }
                      />

                      {/* Chart Board — location-wide view of every resident
                          chart, same direct-Route placement as Activity Hub
                          (must precede the catch-all ResidentRoutes group) */}
                      <Route
                        exact
                        path="/d/chart-board"
                        render={() =>
                          itemValidator(<ChartBoard />, `RESIDENT`, `DISPLAY`)
                        }
                      />

                      {/* Liberty review — home-level AGNI review queue for
                          pre-existing DoLS authorisations. Same direct-Route
                          placement as Chart Board (must precede the catch-all
                          ResidentRoutes group), and gated on RESIDENT like
                          every other home-level clinical register — the
                          Safeguarding and Incidents registers it sits beside
                          use the same item. */}
                      <Route
                        exact
                        path="/d/governance/liberty-review"
                        render={() =>
                          itemValidator(
                            <LibertyReview />,
                            `RESIDENT`,
                            `DISPLAY`,
                          )
                        }
                      />

                      {/* Smart Feedback — "My Feedback" is open to every
                          authenticated employee (same footing as the
                          submission trigger itself, no item_name gate).
                          Review is an internal product-team tool: gated by
                          is_master_admin directly, NOT itemValidator — its
                          bypass-all also covers per-company "administrator"
                          accounts, which would leak cross-tenant data here. */}
                      <Route
                        exact
                        path="/d/feedback/mine"
                        render={() => <MyFeedback />}
                      />
                      <Route
                        exact
                        path="/d/feedback"
                        render={() =>
                          user?.is_master_admin === true ? (
                            <FeedbackReview />
                          ) : (
                            <NoPermission />
                          )
                        }
                      />

                      {/* Resident Routes */}
                      <ResidentRoutes itemValidator={itemValidator} />

                      <Route component={NotFoundPage} />
                    </Switch>
                  </Suspense>
                </DashBoard>
              </PrivateRoute>
            </Switch>
          </MuiPickersUtilsProvider>
        )}
      </Suspense>
    </>
  );
}
RootApp.propTypes = {
  isAuthenticating: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  checkAuth: PropTypes.func.isRequired,
  loadBodyRegions: PropTypes.func.isRequired,
};

const mapStateToProps = ({ AuthReducer }) => ({
  isAuthenticating: AuthReducer.isAuthenticating,
  isAuthenticated: AuthReducer.isAuthenticated,
  permissions: AuthReducer.permissions,
  user: AuthReducer.user,
});
const mapDispatchToProps = (dispatch) => ({
  checkAuth: (...params) => dispatch(check(...params)),
  loadBodyRegions: () => dispatch(fetchBodyRegions()),
});

export default connect(mapStateToProps, mapDispatchToProps)(RootApp);
